/**
 * Mega menu — block-driven navigation.
 * Desktop: hovering "Menu" opens an overlay, items reveal panels. Mobile: left drawer with sliding panels.
 */
import { Component } from '@theme/component';
import { lockScroll, unlockScroll } from '@theme/scrolling';
import { trapFocus } from '@theme/focus';

class MegaMenu extends Component {
  /** @type {string|null} Currently active desktop panel id */
  #activeId = null;
  /** @type {number|null} */
  #closeTimer = null;
  #closeDelay = 150;
  /** @type {{ destroy: () => void }|null} */
  #focusTrap = null;

  get isMobile() {
    return window.matchMedia('(max-width: 1023px)').matches;
  }

  get isOpen() {
    return this.dataset.open === 'true';
  }

  get trigger() {
    return document.querySelector('[aria-controls="mega-menu-root"]');
  }

  connectedCallback() {
    super.connectedCallback();
    // Close synchronously on link click so the page transition's scrollTo(top) wins over unlockScroll.
    this.addEventListener('click', this.#onLinkNavigate);
    this.addEventListener('focusin', this.#onFocusIn);
    // pointermove n'est pas délégué par Component (on:…) : écouteur direct sur le fond.
    this.querySelector('.mega-menu__backdrop')?.addEventListener('pointermove', this.#onBackdropMove);
    // Filet de sécurité : la souris qui bouge sur « Menu » rouvre toujours le menu (pointerenter ne se
    // redéclenche pas tant qu'on reste sur le bouton).
    this.trigger?.addEventListener('pointermove', this.#onTriggerMove);
  }

  #onTriggerMove = (event) => {
    if (event.pointerType === 'touch' || this.isMobile) return;
    if (!this.isOpen || this.#closeTimer) this.open();
  };

  /** Clavier (desktop) : le focus sur une rubrique affiche son panneau, comme le survol. */
  #onFocusIn = (event) => {
    if (this.isMobile || !this.isOpen) return;
    const item = event.target instanceof Element ? event.target.closest('.mega-item__trigger[data-item-id]') : null;
    if (item) this.#activate(item.dataset.itemId);
  };

  /** A real link inside the menu was clicked → close the menu before navigating. */
  #onLinkNavigate = (event) => {
    if (event.defaultPrevented) return; // e.g. a mobile tap that opened a sub-panel
    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (link) this.close();
  };

  // ── Open / close ──

  /** Desktop hover-open (ignored on mobile). */
  open() {
    if (this.isMobile) return;
    // Retour sur « Menu » pendant le délai de fermeture : annuler la fermeture programmée,
    // sinon le menu se fermait sous la souris et ne se rouvrait plus sans ressortir du bouton.
    this.cancelClose();
    this.#doOpen();
  }

  /** Mobile click-toggle ; sur desktop, seulement au clavier (Entrée/Espace : event.detail === 0), la souris passant par le survol. */
  toggle(event) {
    const fromKeyboard = event?.detail === 0;
    if (!this.isMobile && !fromKeyboard) return;
    this.isOpen ? this.#doClose() : this.#doOpen(fromKeyboard);
  }

  #doOpen(fromKeyboard = false) {
    if (this.isOpen) return;
    this.dataset.open = 'true';
    this.trigger?.setAttribute('aria-expanded', 'true');
    lockScroll();

    // Mobile, ou ouverture au clavier sur desktop : le focus entre dans le menu et y reste.
    // On attend la fin du passage visibility hidden → visible (transition CSS), sinon le focus est refusé.
    if (this.isMobile || fromKeyboard) {
      const focusInside = (tries = 0) => {
        if (!this.isOpen || this.#focusTrap) return;
        if (getComputedStyle(this).visibility !== 'visible' && tries < 25) {
          setTimeout(() => focusInside(tries + 1), 20);
          return;
        }
        this.#focusTrap = trapFocus(this);
      };
      focusInside();
    }

    document.addEventListener('keydown', this.#onEscape);
  }

  close() {
    this.#doClose();
  }

  #doClose() {
    if (!this.isOpen) return;
    const hadFocus = this.contains(document.activeElement);
    this.#clearTimer();
    this.dataset.open = 'false';
    this.trigger?.setAttribute('aria-expanded', 'false');
    unlockScroll();
    this.#focusTrap?.destroy();
    this.#focusTrap = null;
    this.#reset();
    document.removeEventListener('keydown', this.#onEscape);
    // Le focus revient sur le bouton « Menu » s'il était dans le menu.
    if (hadFocus) this.trigger?.focus();
  }

  scheduleClose() {
    if (this.isMobile) return;
    this.#clearTimer();
    this.#closeTimer = window.setTimeout(() => this.#doClose(), this.#closeDelay);
  }

  cancelClose() {
    this.#clearTimer();
  }

  /** Schedule close when pointer enters the backdrop — cancelled if re-entering menu content */
  onBackdropEnter(event) {
    if (this.isMobile) return;
    if (this.#inSafeZone(event)) {
      this.cancelClose();
      return;
    }
    this.scheduleClose();
  }

  /** Sur le fond : on suit la souris pour fermer seulement quand elle sort de la zone de passage. */
  #onBackdropMove = (event) => {
    if (this.isMobile || !this.isOpen) return;
    if (this.#inSafeZone(event)) this.cancelClose();
    else if (!this.#closeTimer) this.scheduleClose();
  };

  /**
   * Zone de passage entre le bouton « Menu » et le panneau (grand écran : le bouton est loin du bord gauche) :
   * tout ce qui est à gauche du bouton, sur toute la hauteur, garde le menu ouvert.
   */
  #inSafeZone(event) {
    const trigger = this.trigger?.getBoundingClientRect();
    const panel = this.querySelector('.mega-menu__left')?.getBoundingClientRect();
    if (!trigger || typeof event.clientX !== 'number') return false;
    const limit = Math.max(trigger.right, panel?.right || 0) + 24;
    return event.clientX <= limit;
  }

  #onEscape = (event) => {
    if (event.key === 'Escape') this.#doClose();
  };

  // ── Desktop: reveal panel on hover ──

  activateItem(event) {
    if (this.isMobile) return;
    this.cancelClose();
    const el = event.target instanceof Element ? event.target.closest('[data-item-id]') : null;
    if (!el) return;
    this.#activate(el.dataset.itemId);
  }

  #activate(id) {
    if (!id || id === this.#activeId) return;
    this.#clearActive();
    this.#activeId = id;

    const panel = this.querySelector(`.mega-item__panel[data-panel="${id}"]`);
    panel?.classList.add('is-active');
    panel?.setAttribute('aria-hidden', 'false');

    const trigger = this.querySelector(`.mega-item__trigger[data-item-id="${id}"]`);
    trigger?.classList.add('is-active');
    trigger?.setAttribute('aria-expanded', 'true');
  }

  #clearActive() {
    this.querySelectorAll('.mega-item__panel.is-active').forEach((p) => {
      p.classList.remove('is-active');
      p.setAttribute('aria-hidden', 'true');
    });
    this.querySelectorAll('.mega-item__trigger.is-active').forEach((t) => {
      t.classList.remove('is-active');
      t.setAttribute('aria-expanded', 'false');
    });
    this.#activeId = null;
  }

  // ── Mobile: slide panel in / out ──

  onItemClick(event) {
    const el = event.target instanceof Element ? event.target.closest('[data-item-id]') : null;
    if (!el) return;

    // Desktop: a top-level item with a real link navigates on click.
    if (!this.isMobile) {
      if (el.tagName === 'A' && el.getAttribute('href')) return; // let it navigate
      return;
    }

    // Mobile: slide the panel in instead of navigating.
    const panel = this.querySelector(`.mega-item__panel[data-panel="${el.dataset.itemId}"]`);
    if (!panel) return;
    event.preventDefault();
    panel.classList.add('is-open');
    panel.setAttribute('aria-hidden', 'false');
    el.setAttribute('aria-expanded', 'true');
    // Clavier / lecteur d'écran : le focus suit le panneau (bouton retour), une fois affiché.
    const back = panel.querySelector('.mega-item__back');
    const focusBack = (tries = 0) => {
      if (!panel.classList.contains('is-open')) return;
      if (back && getComputedStyle(panel).visibility !== 'visible' && tries < 25) {
        setTimeout(() => focusBack(tries + 1), 20);
        return;
      }
      back?.focus();
    };
    focusBack();
  }

  closePanel(event) {
    const el = event.target instanceof Element ? event.target.closest('.mega-item__panel') : null;
    const panel = el ?? this.querySelector('.mega-item__panel.is-open');
    if (!panel) return;
    const hadFocus = panel.contains(document.activeElement);
    panel.classList.remove('is-open');
    panel.setAttribute('aria-hidden', 'true');
    const trigger = this.querySelector(`.mega-item__trigger[data-item-id="${panel.dataset.panel}"]`);
    trigger?.setAttribute('aria-expanded', 'false');
    if (hadFocus) trigger?.focus();
  }

  // ── Helpers ──

  #reset() {
    this.#clearActive();
    this.querySelectorAll('.mega-item__panel.is-open').forEach((p) => {
      p.classList.remove('is-open');
      p.setAttribute('aria-hidden', 'true');
    });
  }

  #clearTimer() {
    if (this.#closeTimer) {
      clearTimeout(this.#closeTimer);
      this.#closeTimer = null;
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#clearTimer();
    this.#focusTrap?.destroy();
    this.#focusTrap = null;
    this.removeEventListener('click', this.#onLinkNavigate);
    this.removeEventListener('focusin', this.#onFocusIn);
    this.querySelector('.mega-menu__backdrop')?.removeEventListener('pointermove', this.#onBackdropMove);
    this.trigger?.removeEventListener('pointermove', this.#onTriggerMove);
    document.removeEventListener('keydown', this.#onEscape);
  }
}

if (!customElements.get('mega-menu')) {
  customElements.define('mega-menu', MegaMenu);
}
